import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Gift,
  Package,
  Sparkles,
  Tag,
} from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  formatCurrency,
  formatDateInCompanyTimeZone
} from "../../../../../utils/format";
import { richTextToPlainText } from "../../../../../utils/richText";

const DiscountDisplay = ({ discounts }) => {
  const companyState = useSelector((state) => state?.companyReducer?.company);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDescId, setExpandedDescId] = useState(null);
  
  const activeDiscounts = discounts?.filter(discount => {
    if (discount.status !== "ACTIVE") {
      return false;
    }

    if (!discount.timePeriod?.effectiveDate) return true;

    const now = new Date();
    const start = new Date(discount.timePeriod.effectiveDate.effectiveStartDate);
    const end = new Date(discount.timePeriod.effectiveDate.effectiveEndDate);
    return now >= start && now <= end;
  }) || [];

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return formatDateInCompanyTimeZone(dateString, companyState, "MMM d, yyyy", "");
  };

  const formatDiscountValue = (discount) => {
    if (discount.percentage) return `${discount.percentage}%`;
    if (discount.amount) return formatCurrency(discount.amount, companyState);
    return "DEAL";
  };

  const getDaysRemaining = (timePeriod) => {
    if (!timePeriod?.effectiveDate?.effectiveEndDate) return null;
    const end = new Date(timePeriod.effectiveDate.effectiveEndDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getTheme = (applyType) => {
    switch (applyType) {
      case "PERCENT":
        return {
          gradient: "from-violet-600 to-purple-600",
          lightBg: "bg-violet-50",
          lightText: "text-violet-700",
          lightBorder: "border-violet-200",
          badge: "bg-violet-600",
          ring: "ring-violet-200",
          dot: "bg-violet-500",
        };
      case "AMOUNT":
        return {
          gradient: "from-blue-600 to-cyan-500",
          lightBg: "bg-blue-50",
          lightText: "text-blue-700",
          lightBorder: "border-blue-200",
          badge: "bg-blue-600",
          ring: "ring-blue-200",
          dot: "bg-blue-500",
        };
      default:
        return {
          gradient: "from-emerald-500 to-teal-500",
          lightBg: "bg-emerald-50",
          lightText: "text-emerald-700",
          lightBorder: "border-emerald-200",
          badge: "bg-emerald-600",
          ring: "ring-emerald-200",
          dot: "bg-emerald-500",
        };
    }
  };

  const getUniqueCategories = (products) => {
    if (!products?.length) return [];
    return [...new Set(products.map((p) => p.category?.name).filter(Boolean))];
  };

  // Check if description needs truncation
  const needsTruncation = (description) => {
    return description && description.length > 120; // Adjust character count as needed
  };

  // Show empty state if no active discounts
  if (!activeDiscounts?.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100"
      >
        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <Gift className="w-8 h-8 text-slate-400" />
        </div>
        <p className="text-slate-700 font-semibold text-lg">No active discounts</p>
        <p className="text-slate-400 text-sm mt-1">Check back later for special offers</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3">
      {activeDiscounts.map((discount, index) => {
        const daysLeft = getDaysRemaining(discount.timePeriod);
        const isExpanded = expandedId === (discount.primaryId || index);
        const isDescExpanded = expandedDescId === (discount.primaryId || index);
        const theme = getTheme(discount.applyType);
        const categories = getUniqueCategories(discount.associatedProducts);
        // Stored as rich text - strip the markup before measuring and rendering.
        const descriptionText = richTextToPlainText(discount.description);
        const descriptionNeedsTruncation = needsTruncation(descriptionText);

        return (
          <motion.div
            key={discount.primaryId || index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.07, duration: 0.3 }}
          >
            <div
              className={`relative bg-white rounded-2xl border overflow-hidden transition-all duration-300
                  border-transparent shadow-md ring-2 ${theme.ring}`}
            >
              {/* Top accent bar */}
              <div className={`h-1 w-full bg-gradient-to-r ${theme.gradient}`} />

              <div className="p-5">
                <div className="flex items-start gap-4">
                  {/* Discount value circle */}
                  <div className="flex-shrink-0 relative">
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${theme.gradient} flex flex-col items-center justify-center text-white shadow-lg`}
                    >
                      <span className="text-xl font-black leading-none">
                        {formatDiscountValue(discount)}
                      </span>
                      <span className="text-[10px] font-semibold opacity-90 mt-0.5">OFF</span>
                    </div>
                    {/* Active status indicator */}
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white" />
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-bold text-slate-800 text-base leading-tight">
                          {discount.name}
                        </h4>
                        {discount.company?.name && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-xs text-slate-500 font-medium">
                              {discount.company.name}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {discount.type === "ONE_TIME" && (
                          <span className={`px-2 py-0.5 ${theme.lightBg} ${theme.lightText} text-xs font-semibold rounded-full flex items-center gap-1`}>
                            <Sparkles className="w-3 h-3" />
                            One-time
                          </span>
                        )}
                        {discount.code && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">
                            Code: {discount.code}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Description with Read More toggle */}
                    {descriptionText && (
                      <div className="mt-2">
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={isDescExpanded ? 'expanded' : 'collapsed'}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <p className={`text-slate-500 text-xs leading-relaxed ${!isDescExpanded && descriptionNeedsTruncation ? 'line-clamp-2' : ''}`}>
                              {descriptionText}
                            </p>
                          </motion.div>
                        </AnimatePresence>
                        
                        {descriptionNeedsTruncation && (
                          <button
                            onClick={() => setExpandedDescId(isDescExpanded ? null : (discount.primaryId || index))}
                            className={`flex items-center gap-1 text-xs font-semibold mt-1.5 ${theme.lightText} hover:opacity-80 transition-all`}
                          >
                            {isDescExpanded ? (
                              <>Read less <ChevronUp className="w-3 h-3" /></>
                            ) : (
                              <>Read more <ChevronDown className="w-3 h-3" /></>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Key info pills */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {discount.timePeriod?.effectiveDate && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>
                            {formatDate(discount.timePeriod.effectiveDate.localStartDate)}
                            {" — "}
                            {formatDate(discount.timePeriod.effectiveDate.localEndDate)}
                          </span>
                        </div>
                      )}
                      {daysLeft !== null && daysLeft <= 7 && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg border border-amber-100">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {daysLeft <= 0 ? "Expires today" : `${daysLeft}d left`}
                        </div>
                      )}
                      {discount.criteria?.durationTypes?.length > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span>{discount.criteria.durationTypes.join(", ")} booking</span>
                        </div>
                      )}
                      {categories.length > 0 && (
                        <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border ${theme.lightBg} ${theme.lightText} ${theme.lightBorder}`}>
                          <Tag className="w-3.5 h-3.5" />
                          <span>{categories.join(", ")}</span>
                        </div>
                      )}
                    </div>

                    {/* Footer row */}
                    <div className="flex items-center justify-between mt-3">
                      

                      {discount.associatedProducts?.length > 0 && (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : (discount.primaryId || index))}
                          className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${theme.lightBg} ${theme.lightText} hover:opacity-80`}
                        >
                          <Package className="w-3.5 h-3.5" />
                          {discount.associatedProducts.length} products
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expandable product list */}
                <AnimatePresence>
                  {isExpanded && discount.associatedProducts?.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className={`mt-4 pt-4 border-t ${theme.lightBorder}`}>
                        <p className="text-xs font-semibold text-slate-500 mb-2 tracking-wide">
                          Eligible Products
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {discount.associatedProducts.map((product) => (
                            <div
                              key={product.primaryId}
                              className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-xl border border-slate-100"
                            >
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${theme.dot}`} />
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-700 truncate">{product.name || `Product ID: ${product.primaryId}`}</p>
                                {product.category?.name && (
                                  <p className="text-[10px] text-slate-400 truncate">{product.category.name}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default DiscountDisplay;
